/**
 * Outil « Horloge modulaire » (Boîte à outils, Tle expertes → prépa).
 *
 * Un cadran à n heures. « Restes » : a tourne de q tours puis s'arrête sur
 * r, a ≡ r [n]. « Multiples » : 0, a, 2a… dessinent une étoile qui passe
 * partout seulement si PGCD(a, n) = 1 (a inversible). « Puissances » : les
 * aᵏ finissent par boucler ; si n est premier, a^(n−1) ≡ 1 (Fermat).
 */

import { useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, NumberField, Segmented, Stepper } from '../../ui/controls';
import {
  divmod,
  gcd,
  inverse,
  isPrime,
  mod,
  multiplesOrbit,
  N_MAX,
  N_MIN,
  powerCycle,
  powMod,
} from './modular';

const S = 300;
const C = S / 2;
const R = 112;

type Mode = 'rest' | 'multiples' | 'powers';

function pos(i: number, n: number, r = R) {
  const t = -Math.PI / 2 + (2 * Math.PI * i) / n;
  return { x: C + r * Math.cos(t), y: C + r * Math.sin(t) };
}

/** 11 → ¹¹ */
const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const sup = (k: number) =>
  String(k)
    .split('')
    .map((c) => SUP[Number(c)])
    .join('');

const minus = (v: number) => String(v).replace('-', '−');

const A_MIN = -999;
const A_MAX = 9999;

export function ModularClockTool({ onSend, sendCost }: ToolProps) {
  const [mode, setMode] = useState<Mode>('rest');
  const [n, setN] = useState(12);
  const [a, setA] = useState(47);
  const [base, setBase] = useState(5);
  const b = mod(base, n);

  const { q, r } = divmod(a, n);
  const orbit = multiplesOrbit(b, n);
  const d = gcd(b, n);
  const inv = inverse(b, n);
  const cycle = powerCycle(b, n);
  const prime = isPrime(n);

  // points allumés et cordes selon le mode
  const lit = new Set<number>(mode === 'rest' ? [r] : mode === 'multiples' ? orbit : cycle.values);
  const chords: Array<[number, number]> =
    mode === 'multiples'
      ? orbit.map((x, i) => [x, orbit[(i + 1) % orbit.length]!])
      : mode === 'powers'
        ? cycle.values.map((x, i) => [
            x,
            i + 1 < cycle.values.length ? cycle.values[i + 1]! : cycle.values[cycle.start]!,
          ])
        : [];

  const dot = Math.max(8, Math.min(15, (Math.PI * R) / n - 2));
  const font = n > 16 ? 12 : 14;

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Mode"
        value={mode}
        options={[
          { value: 'rest', label: 'Restes' },
          { value: 'multiples', label: 'Multiples' },
          { value: 'powers', label: 'Puissances' },
        ]}
        onChange={setMode}
      />

      <div className="grid grid-cols-2 gap-2">
        <Stepper label="n (heures)" value={n} min={N_MIN} max={N_MAX} onChange={setN} />
        {mode === 'rest' ? (
          <NumberField label="a" value={a} min={A_MIN} max={A_MAX} onChange={setA} />
        ) : (
          <Stepper
            label={mode === 'multiples' ? 'a (pas)' : 'a (base)'}
            value={b}
            min={0}
            max={n - 1}
            onChange={setBase}
          />
        )}
      </div>

      <div className="mx-auto w-full max-w-[22rem]">
        <svg
          viewBox={`0 0 ${S} ${S}`}
          className="block w-full select-none"
          role="img"
          aria-label={`Cadran à ${n} heures, de 0 à ${n - 1}.`}
        >
          <circle cx={C} cy={C} r={R} fill="none" stroke="var(--color-ink-200)" strokeWidth={2} />
          {chords.map(([x, y], i) => {
            const p = pos(x, n);
            const p2 = pos(y, n);
            return (
              <line
                key={`c${i}`}
                x1={p.x}
                y1={p.y}
                x2={p2.x}
                y2={p2.y}
                stroke={mode === 'powers' ? 'var(--color-accent-500)' : 'var(--color-primary-500)'}
                strokeWidth={2}
                strokeOpacity={0.7}
              />
            );
          })}
          {mode === 'rest' && (
            <>
              <line
                x1={C}
                y1={C}
                x2={pos(r, n, R - dot - 4).x}
                y2={pos(r, n, R - dot - 4).y}
                stroke="var(--color-primary-500)"
                strokeWidth={4}
                strokeLinecap="round"
              />
              <circle cx={C} cy={C} r={6} fill="var(--color-primary-500)" />
              <text
                x={C}
                y={C + 34}
                textAnchor="middle"
                fontSize={14}
                fontWeight={600}
                fill="var(--color-ink-500)"
              >
                {`q = ${minus(q)} tour${Math.abs(q) > 1 ? 's' : ''}, puis ${r}`}
              </text>
            </>
          )}
          {Array.from({ length: n }, (_, i) => {
            const p = pos(i, n);
            const on = lit.has(i);
            return (
              <g key={i} data-testid={on ? 'clock-lit' : undefined}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={dot}
                  fill={on ? 'var(--color-primary-500)' : 'var(--color-ink-100)'}
                />
                <text
                  x={p.x}
                  y={p.y + font * 0.36}
                  textAnchor="middle"
                  fontSize={font}
                  fontWeight={on ? 700 : 500}
                  fill={on ? '#fff' : 'var(--color-ink-700)'}
                >
                  {i}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {mode === 'rest' && (
        <section
          className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
          aria-live="polite"
          data-testid="clock-rest"
        >
          <div className="flex min-h-11 items-center gap-2">
            <MathText className="text-ink-900 min-w-0 flex-1 overflow-x-auto text-lg">
              {`$${a} \\equiv ${r} \\ [${n}]$`}
            </MathText>
            {onSend && (
              <SendToSheetButton
                label={String(r)}
                cost={sendCost}
                onClick={() => onSend({ atoms: [String(r)] })}
              />
            )}
          </div>
          <p className="text-ink-600 text-sm">
            Division euclidienne : {minus(a)} = {q < 0 ? `(${minus(q)})` : q} × {n} + {r}, avec 0 ≤{' '}
            {r} &lt; {n}.
            {a < 0 &&
              ` Pour un nombre négatif, le reste reste positif : on recule de ${-q} tour${-q > 1 ? 's' : ''} entier${-q > 1 ? 's' : ''}, puis on avance de ${r}.`}
          </p>
        </section>
      )}

      {mode === 'multiples' && (
        <Note tone={d === 1 ? 'success' : 'neutral'}>
          <span className="block" data-testid="clock-multiples">
            Multiples de {b} modulo {n} : {orbit.join(', ')}. Ils visitent {orbit.length} point
            {orbit.length > 1 ? 's' : ''} sur {n}, car PGCD({b} ; {n}) = {d}.
          </span>
          <span className="mt-1 block">
            {inv !== null
              ? `${b} est inversible modulo ${n} : ${b} × ${inv} = ${b * inv} ≡ 1 [${n}].`
              : `${b} n’est pas inversible modulo ${n} : aucun multiple ne tombe sur 1.`}
          </span>
        </Note>
      )}

      {mode === 'powers' && (
        <section className="space-y-2" aria-live="polite">
          <div className="flex flex-wrap gap-2" data-testid="clock-powers">
            {cycle.values.map((v, k) => (
              <span
                key={k}
                className={cn(
                  'rounded-xl px-2 py-1 text-sm',
                  k >= cycle.start ? 'bg-accent-50 text-ink-900' : 'bg-ink-100 text-ink-700',
                )}
              >
                <MathText>{`$${b}^{${k}} \\equiv ${v}$`}</MathText>
              </span>
            ))}
          </div>
          <Note tone={prime && b !== 0 ? 'success' : 'neutral'}>
            <span className="block">
              Les puissances de {b} bouclent avec une période de {cycle.period}
              {cycle.start > 0 ? ` à partir de ${b}${sup(cycle.start)}` : ''}.
            </span>
            <span className="mt-1 block">
              {prime
                ? b === 0
                  ? `${n} est premier, mais 0 est un multiple de ${n} : le petit théorème de Fermat ne s’applique pas.`
                  : `${n} est premier et ne divise pas ${b} : ${b}${sup(n - 1)} ≡ ${powMod(b, n - 1, n)} [${n}] (petit théorème de Fermat).`
                : `${n} n’est pas premier : ${b}${sup(n - 1)} ≡ ${powMod(b, n - 1, n)} [${n}], Fermat ne promet rien.`}
            </span>
          </Note>
        </section>
      )}
    </div>
  );
}
